/** 電圧階級別 (特別高圧/高圧/低圧) と実績の 1 コマぶんの値。 */
export type VoltageBreakdownKoma = {
  demandActual: number | null
  demandExtraHigh: number | null
  demandHigh: number | null
  demandLow: number | null
}

/** 内訳の和と実績の相対許容誤差。BFF が Decimal を float64 に落とすため厳密一致しない。 */
const SUM_TOLERANCE_RATIO = 1e-6

/**
 * 電圧階級別の積み上げへ切り替えてよいか。実績値を持つ全コマで「内訳の和 = 実績」が
 * 成り立つ時だけ true を返す。
 *
 * 電圧階級別は「実績を 3 分割する」表示 (doji-bg.md AC-BDB-026) なので、切り替えると
 * 実績エリアは隠れる。内訳が 1 つでもあれば切り替える判定にすると、階級行が丸ごと
 * 欠けたエリアや一部コマだけ階級が欠けた日 (託送実績は速報→確報の 3 段階で到着する)
 * で、総量より小さい面が実績として描かれる。当日需給監視はこの面から不足/余りを
 * 読んで追加調達を判断する (AC-BDB-030) ため、過小表示は逆方向の判断を招く。
 *
 * 和が合わないコマが 1 つでもあれば false を返し、呼び出し側は通常の実績エリアを
 * 維持する。内訳が 1 コマも無い場合も false (= 切り替える材料が無い)。
 *
 * 実績が無いコマ (未着 / 全損 / 未来) は判定から除く。内訳も無いのが正常な状態で、
 * ここを不一致に数えると当日の進行中コマで積み上げが一切出なくなる。
 */
export function hasVoltageBreakdown(komas: VoltageBreakdownKoma[]): boolean {
  let hasAnyBreakdown = false

  for (const koma of komas) {
    if (koma.demandActual === null) continue

    const parts = [
      koma.demandExtraHigh,
      koma.demandHigh,
      koma.demandLow,
    ].filter((v): v is number => v !== null)
    if (parts.length === 0) return false

    hasAnyBreakdown = true
    const sum = parts.reduce((acc, v) => acc + v, 0)
    const tolerance = Math.abs(koma.demandActual) * SUM_TOLERANCE_RATIO
    if (Math.abs(sum - koma.demandActual) > tolerance) return false
  }

  return hasAnyBreakdown
}

/** 電圧階級の識別子。積み上げの上から順に 特別高圧 → 高圧 → 低圧。 */
export type VoltageClass = 'extraHigh' | 'high' | 'low'

/**
 * 積み上げの最上段に来る電圧階級を返す。design `js/charts.js` の `_voltTop` と同じ判定。
 *
 * 帯の境界線は「最上段だけ自色の輪郭、それ以外はカード地色で 1px 抜く」形で描くため
 * (`BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR` の godoc 参照)、最上段を取り違えるとスタックの
 * 外周まで地色で抜けて帯がカードに溶ける。
 *
 * 最上段は「実際に描かれる階級のうち一番上」であって固定ではない。トグルの ON/OFF と
 * データの実在の積で決める — 特別高圧を OFF にした時や、特別高圧の値を持たないエリアでは
 * 高圧が最上段になる。
 *
 * @param komas 判定対象のコマ列 (階級ごとの値の実在を見る)
 * @param visible 階級ごとの表示トグル (OFF の階級は描かれないので最上段になり得ない)
 */
export function topVoltageClass(
  komas: VoltageBreakdownKoma[],
  visible: Record<VoltageClass, boolean>,
): VoltageClass {
  if (visible.extraHigh && komas.some((k) => k.demandExtraHigh !== null)) {
    return 'extraHigh'
  }
  if (visible.high && komas.some((k) => k.demandHigh !== null)) {
    return 'high'
  }
  return 'low'
}
